import React, { useState, useEffect } from 'react';
import { MANDATORY_MODULES } from '../constantsMandatory';
import { MandatoryModuleInfo, MandatoryModuleId, MandatoryTrainingRecord, MandatorySettings } from '../types';
import { SAFETY_INDUCTION_PDF_URL } from '../constants';
import { saveMandatoryTrainingRecord, getMandatoryRecordsByNik, subscribeMandatorySettings, searchMandatoryWorkersByName, searchMandatoryWorkersByNik, DEFAULT_MANDATORY_SETTINGS, WorkerSuggestion } from '../services/storageService';
import { generateMandatoryTrainingPDF } from '../services/pdfService';
import { PdfViewer } from './PdfViewer';
import { 
  BookOpen, CheckCircle, AlertCircle, ArrowLeft, ArrowRight, Award, 
  FileText, User, Building, CreditCard, Clock, RotateCcw, Download,
  Shield, Check, Sparkles, Loader2, HelpCircle, Briefcase, MapPin
} from 'lucide-react';

interface MandatoryTrainingProps {
  lang: 'id' | 'en';
  onBackToMain: () => void;
}

export const MandatoryTraining: React.FC<MandatoryTrainingProps> = ({
  lang,
  onBackToMain
}) => {
  const [selectedModuleId, setSelectedModuleId] = useState<MandatoryModuleId | null>(null);
  const [currentStep, setCurrentStep] = useState<'IDENTITY' | 'SLIDES' | 'QUIZ' | 'RESULT'>('IDENTITY');
  const [mandatorySettings, setMandatorySettings] = useState<MandatorySettings>(DEFAULT_MANDATORY_SETTINGS);

  // Identity Form State
  const [identity, setIdentity] = useState({
    fullName: '',
    nik: '',
    position: '',
    company: '',
    workArea: 'Yard Sukapura',
    customWorkArea: '',
    completionDate: new Date().toISOString().split('T')[0]
  });

  // Quiz Answers State
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [testResult, setTestResult] = useState<{ score: number; passed: boolean; totalPoints: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedRecord, setSavedRecord] = useState<MandatoryTrainingRecord | null>(null);
  const [userRecords, setUserRecords] = useState<MandatoryTrainingRecord[]>([]);

  // Draft restored banner
  const [draftRestored, setDraftRestored] = useState(false);

  // Worker name autocomplete
  const [workerSuggestions, setWorkerSuggestions] = useState<WorkerSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const suggestionTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Worker NIK autocomplete
  const [nikSuggestions, setNikSuggestions] = useState<WorkerSuggestion[]>([]);
  const [showNikSuggestions, setShowNikSuggestions] = useState(false);
  const [nikSuggestionsLoading, setNikSuggestionsLoading] = useState(false);
  const nikSuggestionTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active Module, Custom Questions, & PDF Url
  const activeModule = selectedModuleId ? MANDATORY_MODULES.find(m => m.id === selectedModuleId) : null;

  const effectiveQuestions = (selectedModuleId && mandatorySettings.customQuestions?.[selectedModuleId] && mandatorySettings.customQuestions[selectedModuleId].length >= 10)
    ? mandatorySettings.customQuestions[selectedModuleId]
    : (activeModule?.questions || []);

  const activePdfUrl = selectedModuleId 
    ? ((mandatorySettings.modulePdfs?.[selectedModuleId] && !mandatorySettings.modulePdfs[selectedModuleId].startsWith('/modules/'))
        ? mandatorySettings.modulePdfs[selectedModuleId] 
        : SAFETY_INDUCTION_PDF_URL)
    : '';

  // Subscribe to settings in real-time so question/module changes appear instantly
  useEffect(() => {
    let initialAreas: string[] | null = null;
    const unsub = subscribeMandatorySettings((st) => {
      if (st) {
        setMandatorySettings(st);
        if (!initialAreas && st.allowedWorkAreas && st.allowedWorkAreas.length > 0) {
          initialAreas = st.allowedWorkAreas;
          setIdentity(prev => ({
            ...prev,
            workArea: prev.workArea || st.allowedWorkAreas[0]
          }));
        }
      }
    });
    return () => {
      unsub();
    };
  }, []);

  // Fetch only this participant's records by NIK (saving 98%+ Firebase reads)
  useEffect(() => {
    const cleanNik = identity.nik?.trim();
    if (cleanNik && cleanNik.length >= 3) {
      let cancelled = false;
      getMandatoryRecordsByNik(cleanNik).then(recs => {
        if (!cancelled) {
          setUserRecords(recs);
        }
      });
      return () => {
        cancelled = true;
      };
    } else {
      setUserRecords([]);
    }
  }, [identity.nik]);

  // Restore draft from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('ut_mandatory_training_draft_v1');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.identity && (parsed.identity.fullName || parsed.identity.nik)) {
          setIdentity(parsed.identity);
          if (parsed.selectedModuleId) setSelectedModuleId(parsed.selectedModuleId);
          if (parsed.currentStep) setCurrentStep(parsed.currentStep);
          if (parsed.answers) setAnswers(parsed.answers);
          setDraftRestored(true);
        }
      } catch (e) {
        console.error("Error restoring draft:", e);
      }
    }
  }, []);

  // Auto-save draft on state changes
  useEffect(() => {
    if (currentStep === 'RESULT') {
      localStorage.removeItem('ut_mandatory_training_draft_v1');
      return;
    }

    if (identity.fullName || identity.nik || Object.keys(answers).length > 0) {
      const draft = {
        identity,
        selectedModuleId,
        currentStep,
        answers
      };
      localStorage.setItem('ut_mandatory_training_draft_v1', JSON.stringify(draft));
    }
  }, [identity, selectedModuleId, currentStep, answers]);

  const handleSelectModule = (modId: MandatoryModuleId) => {
    setSelectedModuleId(modId);
    setAnswers({});
    setTestResult(null);
    setSavedRecord(null);
    setCurrentStep('IDENTITY');
  };

  const handleIdentitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalArea = identity.workArea === 'Lainnya' ? identity.customWorkArea : identity.workArea;
    if (!identity.fullName || !identity.nik || !identity.position || !identity.company || !finalArea) {
      alert(lang === 'id' 
        ? 'Mohon lengkapi Nama Lengkap, NRP/NIK, Posisi, Perusahaan, dan Area Kerja!' 
        : 'Please fill Full Name, NRP/NIK, Position, Company, and Work Area!');
      return;
    }
    setCurrentStep('SLIDES');
  };

  const handleQuizOptionSelect = (qId: string, optIdx: number) => {
    setAnswers(prev => ({ ...prev, [qId]: optIdx }));
  };

  const handleSubmitQuiz = async () => {
    if (!activeModule) return;

    // Verify all questions answered
    const unanswered = effectiveQuestions.filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      alert(lang === 'id' 
        ? `Mohon jawab semua ${effectiveQuestions.length} pertanyaan evaluasi!` 
        : `Please answer all ${effectiveQuestions.length} test questions!`);
      return;
    }

    setIsSubmitting(true);

    try {
      let earnedPoints = 0;
      let totalPoints = 0;

      effectiveQuestions.forEach(q => {
        totalPoints += (q.points || 20);
        if (answers[q.id] === q.correctOptionIndex) {
          earnedPoints += (q.points || 20);
        }
      });

      const score = Math.round((earnedPoints / (totalPoints || 1)) * 100);
      const passThreshold = mandatorySettings?.passScore ?? 70;
      const passed = score >= passThreshold;

      const finalArea = identity.workArea === 'Lainnya' ? (identity.customWorkArea || 'Lainnya') : identity.workArea;

      const record: MandatoryTrainingRecord = {
        id: `MT-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        fullName: identity.fullName,
        nik: identity.nik,
        position: identity.position,
        company: identity.company,
        workArea: finalArea,
        moduleId: activeModule.id,
        moduleTitle: activeModule.title,
        completionDate: identity.completionDate,
        timestamp: Date.now(),
        score,
        passed,
        answers
      };

      await saveMandatoryTrainingRecord(record);
      setSavedRecord(record);
      setUserRecords(prev => [record, ...prev.filter(r => r.id !== record.id)]);
      setTestResult({ score, passed, totalPoints });
      setCurrentStep('RESULT');
      localStorage.removeItem('ut_mandatory_training_draft_v1');
    } catch (err) {
      console.error("Error submitting mandatory training quiz:", err);
      alert(lang === 'id' ? 'Gagal menyimpan hasil evaluasi. Coba lagi.' : 'Failed to save evaluation results. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearDraft = () => {
    localStorage.removeItem('ut_mandatory_training_draft_v1');
    setIdentity({
      fullName: '',
      nik: '',
      position: '',
      company: '',
      workArea: 'Yard Sukapura',
      customWorkArea: '',
      completionDate: new Date().toISOString().split('T')[0]
    });
    setAnswers({});
    setSelectedModuleId(null);
    setCurrentStep('IDENTITY');
    setDraftRestored(false);
  };

  const handleFullNameChange = (value: string) => {
    setIdentity(prev => ({ ...prev, fullName: value }));
    setShowSuggestions(true);

    if (suggestionTimerRef.current) {
      clearTimeout(suggestionTimerRef.current);
    }

    if (value.trim().length < 2) {
      setWorkerSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    setSuggestionsLoading(true);
    suggestionTimerRef.current = setTimeout(async () => {
      const results = await searchMandatoryWorkersByName(value);
      setWorkerSuggestions(results);
      setSuggestionsLoading(false);
    }, 350);
  };

  const handleSelectSuggestion = (worker: WorkerSuggestion) => {
    setIdentity(prev => ({
      ...prev,
      fullName: worker.fullName,
      nik: worker.nik,
      position: worker.position,
      company: worker.company,
      workArea: worker.workArea || prev.workArea
    }));
    setShowSuggestions(false);
    setWorkerSuggestions([]);
    setShowNikSuggestions(false);
    setNikSuggestions([]);
  };

  const handleNikChange = (value: string) => {
    setIdentity(prev => ({ ...prev, nik: value }));
    setShowNikSuggestions(true);

    if (nikSuggestionTimerRef.current) {
      clearTimeout(nikSuggestionTimerRef.current);
    }

    if (value.trim().length < 2) {
      setNikSuggestions([]);
      setNikSuggestionsLoading(false);
      return;
    }

    setNikSuggestionsLoading(true);
    nikSuggestionTimerRef.current = setTimeout(async () => {
      const results = await searchMandatoryWorkersByNik(value);
      setNikSuggestions(results);
      setNikSuggestionsLoading(false);
    }, 350);
  };

  // Helper to check if a module was completed by the current NIK/user
  const getModuleStatusForUser = (modId: MandatoryModuleId) => {
    if (!identity.nik) return null;
    const match = userRecords.find(r => r.nik.trim().toLowerCase() === identity.nik.trim().toLowerCase() && r.moduleId === modId && r.passed);
    return match || null;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8 space-y-6 animate-fade-in">
      
      {/* Top Breadcrumb & Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl shadow-xs border border-gray-200">
        <button
          onClick={() => {
            if (selectedModuleId && currentStep !== 'IDENTITY') {
              setCurrentStep('IDENTITY');
            } else if (selectedModuleId) {
              setSelectedModuleId(null);
            } else {
              onBackToMain();
            }
          }}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>
            {selectedModuleId 
              ? (lang === 'id' ? 'Kembali ke Modul' : 'Back to Modules')
              : (lang === 'id' ? 'Kembali ke Menu Utama' : 'Back to Main Menu')}
          </span>
        </button>

        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
          <span>Portal Safety</span>
          <span>/</span>
          <span className="text-amber-600 font-bold">Mandatory Training (7 Modul)</span>
          {activeModule && (
            <>
              <span>/</span>
              <span className="text-gray-900 font-bold truncate max-w-[180px]">{activeModule.title}</span>
            </>
          )}
        </div>
      </div>

      {/* Draft Restored Banner */}
      {draftRestored && currentStep !== 'RESULT' && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-xl text-amber-700 shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-amber-900">
                {lang === 'id' ? 'Melanjutkan Progres Training Sebelumnya' : 'Resuming Saved Training Draft'}
              </h4>
              <p className="text-xs text-amber-800 mt-0.5">
                {lang === 'id'
                  ? 'Data pengerjaan modul & jawaban kuis Anda berhasil dipulihkan secara otomatis.'
                  : 'Your module progress & answers have been automatically restored.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setDraftRestored(false)}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg transition"
            >
              {lang === 'id' ? 'Lanjutkan' : 'Continue'}
            </button>
            <button
              onClick={handleClearDraft}
              className="px-3 py-1.5 bg-white border border-amber-300 text-amber-800 text-xs font-bold rounded-lg hover:bg-amber-100 transition"
            >
              {lang === 'id' ? 'Mulai Baru' : 'Start Fresh'}
            </button>
          </div>
        </div>
      )}

      {/* VIEW 1: MODULE SELECTION GRID */}
      {!selectedModuleId && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-amber-500 via-orange-600 to-amber-700 rounded-3xl p-6 sm:p-8 text-white shadow-lg relative overflow-hidden">
            <div className="relative z-10 space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold backdrop-blur-md">
                <Award className="w-4 h-4 text-amber-200" />
                <span>Safety Compliance Mandatory Program</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                {lang === 'id' ? '7 Modul Safety Mandatory Training' : '7 Mandatory Safety Training Modules'}
              </h2>
              <p className="text-amber-100 text-sm max-w-2xl leading-relaxed">
                {lang === 'id'
                  ? 'Setiap pekerja dan staf wajib membaca materi edukasi serta lulus post-test pada ke-7 modul keselamatan kerja di bawah ini.'
                  : 'All operational staff must complete reading and pass post-tests for all 7 mandatory safety modules below.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MANDATORY_MODULES.map((mod, idx) => {
              const statusRecord = getModuleStatusForUser(mod.id);
              return (
                <div
                  key={mod.id}
                  onClick={() => handleSelectModule(mod.id)}
                  className="bg-white rounded-3xl p-6 border border-gray-200 hover:border-amber-500 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between group transform hover:-translate-y-1 relative overflow-hidden"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-extrabold border border-amber-200 uppercase tracking-wider">
                        {mod.badge}
                      </span>
                      {statusRecord ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>Lulus ({statusRecord.score} pts)</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          <span>~{mod.estimatedMinutes} menit</span>
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-amber-600 transition-colors leading-snug">
                        {mod.title}
                      </h3>
                      <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">
                        {mod.description}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-bold text-amber-600 group-hover:text-amber-700 flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4" />
                      <span>{lang === 'id' ? 'Buka & Kerjakan' : 'Open Module'}</span>
                    </span>
                    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VIEW 2: ACTIVE MODULE CONTENT */}
      {activeModule && (
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden">
          
          {/* Module Header Bar */}
          <div className="bg-gradient-to-r from-slate-900 via-gray-900 to-amber-950 p-6 text-white border-b border-gray-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold border border-amber-500/30 mb-2">
                  <span>{activeModule.badge}</span>
                  <span>•</span>
                  <span>{activeModule.subtitle}</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-white">
                  {activeModule.title}
                </h2>
              </div>

              {/* Step Wizard Indicator */}
              <div className="flex items-center gap-2 bg-gray-800/80 p-2 rounded-2xl border border-gray-700 self-start sm:self-auto">
                <div className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${currentStep === 'IDENTITY' ? 'bg-amber-500 text-white shadow-xs' : 'text-gray-400'}`}>
                  1. Identitas
                </div>
                <div className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${currentStep === 'SLIDES' ? 'bg-amber-500 text-white shadow-xs' : 'text-gray-400'}`}>
                  2. Materi Modul (PDF/Drive)
                </div>
                <div className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${currentStep === 'QUIZ' || currentStep === 'RESULT' ? 'bg-amber-500 text-white shadow-xs' : 'text-gray-400'}`}>
                  3. Post-Test
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            
            {/* STEP 1: WORKER IDENTITY FORM */}
            {currentStep === 'IDENTITY' && (
              <form onSubmit={handleIdentitySubmit} className="max-w-2xl mx-auto space-y-6">
                <div className="text-center space-y-2 mb-8">
                  <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center mx-auto shadow-xs">
                    <User className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {lang === 'id' ? 'Data Diri Pekerja / Peserta' : 'Participant Details'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {lang === 'id' 
                      ? 'Isi nama, NIK, dan perusahaan Anda dengan benar sebelum memulai pembelajaran.' 
                      : 'Please enter your details accurately before starting the module.'}
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">
                      {lang === 'id' ? 'Nama Lengkap Pekerja' : 'Full Name'} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="w-5 h-5 absolute left-3.5 top-3.5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={identity.fullName}
                        onChange={(e) => handleFullNameChange(e.target.value)}
                        onFocus={() => workerSuggestions.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        placeholder="Contoh: Budi Miradi"
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm font-semibold text-gray-800"
                        autoComplete="off"
                      />
                      {showSuggestions && (identity.fullName.trim().length >= 2) && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                          {suggestionsLoading ? (
                            <div className="px-4 py-3 text-xs text-gray-500 flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Mencari data pekerja...</span>
                            </div>
                          ) : workerSuggestions.length > 0 ? (
                            workerSuggestions.map((worker, idx) => (
                              <button
                                key={`${worker.nik}-${idx}`}
                                type="button"
                                onMouseDown={() => handleSelectSuggestion(worker)}
                                className="w-full text-left px-4 py-2.5 hover:bg-amber-50 transition border-b border-gray-100 last:border-b-0 cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                    <User className="w-4 h-4" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-gray-900 truncate">{worker.fullName}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                      NIK: {worker.nik} {worker.position && `· ${worker.position}`}
                                    </p>
                                    <p className="text-xs text-gray-400 truncate">{worker.company} {worker.workArea && `· ${worker.workArea}`}</p>
                                  </div>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-xs text-gray-400">
                              Tidak ada data pekerja ditemukan. Isi manual data diri Anda.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">
                        {lang === 'id' ? 'NRP / NIK Karyawan' : 'NRP / Employee ID'} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <CreditCard className="w-5 h-5 absolute left-3.5 top-3.5 text-gray-400" />
                        <input
                          type="text"
                          required
                          value={identity.nik}
                          onChange={(e) => handleNikChange(e.target.value)}
                          onFocus={() => nikSuggestions.length > 0 && setShowNikSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowNikSuggestions(false), 200)}
                          placeholder="Contoh: 90119013"
                          className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm font-semibold text-gray-800"
                          autoComplete="off"
                        />
                        {showNikSuggestions && (identity.nik.trim().length >= 2) && (
                          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                            {nikSuggestionsLoading ? (
                              <div className="px-4 py-3 text-xs text-gray-500 flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Mencari data pekerja...</span>
                              </div>
                            ) : nikSuggestions.length > 0 ? (
                              nikSuggestions.map((worker, idx) => (
                                <button
                                  key={`${worker.nik}-${idx}`}
                                  type="button"
                                  onMouseDown={() => handleSelectSuggestion(worker)}
                                  className="w-full text-left px-4 py-2.5 hover:bg-amber-50 transition border-b border-gray-100 last:border-b-0 cursor-pointer"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                      <CreditCard className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-bold text-gray-900 truncate">{worker.fullName}</p>
                                      <p className="text-xs text-gray-500 truncate">
                                        NIK: {worker.nik} {worker.position && `· ${worker.position}`}
                                      </p>
                                      <p className="text-xs text-gray-400 truncate">{worker.company} {worker.workArea && `· ${worker.workArea}`}</p>
                                    </div>
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-xs text-gray-400">
                                Tidak ada data pekerja ditemukan dengan NIK tersebut.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">
                        {lang === 'id' ? 'Posisi / Jabatan' : 'Job Position'} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Briefcase className="w-5 h-5 absolute left-3.5 top-3.5 text-gray-400" />
                        <input
                          type="text"
                          required
                          value={identity.position}
                          onChange={(e) => setIdentity(prev => ({ ...prev, position: e.target.value }))}
                          placeholder="Contoh: Member / Leader Receiving"
                          className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm font-semibold text-gray-800"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">
                      {lang === 'id' ? 'Perusahaan / Departemen' : 'Company / Department'} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Building className="w-5 h-5 absolute left-3.5 top-3.5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={identity.company}
                        onChange={(e) => setIdentity(prev => ({ ...prev, company: e.target.value }))}
                        placeholder="Contoh: PT Kamaju"
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">
                      {lang === 'id' ? 'Area Kerja' : 'Work Area'} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <MapPin className="w-5 h-5 absolute left-3.5 top-3.5 text-gray-400 pointer-events-none" />
                      <select
                        value={identity.workArea}
                        onChange={(e) => setIdentity(prev => ({ ...prev, workArea: e.target.value }))}
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm font-semibold text-gray-800 bg-white"
                      >
                        {(mandatorySettings?.allowedWorkAreas || ['Yard Sukapura', 'Yard Cakung', 'Yard Jababeka', 'Used']).map(area => (
                          <option key={area} value={area}>{area}</option>
                        ))}
                        <option value="Lainnya">Lainnya / Other...</option>
                      </select>
                    </div>

                    {identity.workArea === 'Lainnya' && (
                      <div className="mt-2">
                        <input
                          type="text"
                          required
                          value={identity.customWorkArea}
                          onChange={(e) => setIdentity(prev => ({ ...prev, customWorkArea: e.target.value }))}
                          placeholder="Tuliskan Area Kerja Anda..."
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-xs font-semibold text-gray-800"
                        />
                      </div>
                    )}
                  </div>
                </div>                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 text-sm cursor-pointer"
                  >
                    <span>{lang === 'id' ? 'Buka Materi Modul (PDF / Drive) →' : 'Open Module Material (PDF / Drive) →'}</span>
                  </button>
                </div>
              </form>
            )}

            {/* STEP 2: PDF / GOOGLE DRIVE MATERIAL PREVIEW */}
            {currentStep === 'SLIDES' && activeModule && (
              <div className="space-y-6 max-w-5xl mx-auto">
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-6 space-y-6 shadow-xl">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-2xl border border-amber-500/30">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-extrabold uppercase border border-amber-500/30">
                            {activeModule.badge}
                          </span>
                          <h3 className="font-extrabold text-white text-base sm:text-lg">
                            Materi Dokumen: {activeModule.title}
                          </h3>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {activeModule.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <a
                        href={activePdfUrl || SAFETY_INDUCTION_PDF_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                      >
                        <span>Buka / Download Link Materi</span>
                      </a>
                    </div>
                  </div>

                  {/* Live Google Drive / PDF Viewer */}
                  <div className="w-full rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-2xl">
                    <PdfViewer
                      url={activePdfUrl || SAFETY_INDUCTION_PDF_URL}
                      title={`Materi Dokumen ${activeModule.title}`}
                      height="650px"
                    />
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-800">
                    <p className="text-xs text-slate-400">
                      Silakan pelajari dokumen materi di atas melalui Google Drive / Viewer sebelum melanjutkan ke Post-Test Evaluasi.
                    </p>

                    <button
                      onClick={() => setCurrentStep('QUIZ')}
                      className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs sm:text-sm font-extrabold transition flex items-center justify-center gap-2 shadow-lg cursor-pointer animate-pulse shrink-0"
                    >
                      <Award className="w-4 h-4" />
                      <span>{lang === 'id' ? 'Lanjut ke Post-Test Evaluasi →' : 'Proceed to Post-Test →'}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: POST-TEST QUIZ */}
            {currentStep === 'QUIZ' && activeModule && (
              <div className="space-y-8 max-w-3xl mx-auto">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-amber-600" />
                    <span className="text-xs font-bold text-amber-900">
                      {lang === 'id' 
                        ? `Soal Post-Test (${effectiveQuestions.length} Soal - Standar Kelulusan Minimal ${mandatorySettings?.passScore ?? 70}%)` 
                        : `Post-Test (${effectiveQuestions.length} Questions - Pass mark >= ${mandatorySettings?.passScore ?? 70}%)`}
                    </span>
                  </div>
                  <span className="text-xs font-bold bg-amber-200 text-amber-900 px-3 py-1 rounded-full">
                    Terjawab: {Object.keys(answers).length} / {effectiveQuestions.length}
                  </span>
                </div>

                <div className="space-y-6">
                  {effectiveQuestions.map((q, qIdx) => (
                    <div key={q.id || qIdx} className="bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
                      <div className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-amber-600 text-white font-extrabold text-xs flex items-center justify-center shrink-0 mt-0.5">
                          {qIdx + 1}
                        </span>
                        <h4 className="text-sm font-bold text-gray-900 leading-relaxed pt-1">
                          {q.text}
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 gap-2.5 pl-10">
                        {q.options.map((opt, optIdx) => {
                          const isSelected = answers[q.id] === optIdx;
                          const optionLetter = String.fromCharCode(65 + optIdx);
                          return (
                            <button
                              key={optIdx}
                              type="button"
                              onClick={() => handleQuizOptionSelect(q.id, optIdx)}
                              className={`w-full text-left p-3.5 rounded-xl text-xs font-medium border transition flex items-start gap-3 cursor-pointer ${
                                isSelected 
                                  ? 'bg-amber-500 text-white border-amber-600 shadow-sm' 
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400 hover:bg-amber-50/50'
                              }`}
                            >
                              <span className={`w-5 h-5 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${
                                isSelected ? 'bg-white text-amber-600' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {optionLetter}
                              </span>
                              <span className="pt-0.5 leading-relaxed">{opt}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-4 flex items-center justify-between border-t border-gray-200">
                  <button
                    onClick={() => setCurrentStep('SLIDES')}
                    className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-xs font-bold hover:bg-gray-50 transition"
                  >
                    ← Kembalikan ke Slide
                  </button>

                  <button
                    onClick={handleSubmitQuiz}
                    disabled={isSubmitting}
                    className="px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    <span>{isSubmitting ? 'Memproses Evaluasi...' : 'Kirim & Lihat Hasil Evaluasi'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: RESULT & CERTIFICATE */}
            {currentStep === 'RESULT' && testResult && (
              <div className="space-y-8 max-w-2xl mx-auto text-center animate-fade-in">
                
                <div className={`p-8 rounded-3xl border-2 space-y-4 ${
                  testResult.passed 
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-900' 
                    : 'bg-red-50 border-red-300 text-red-900'
                }`}>
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto text-white shadow-md ${
                    testResult.passed ? 'bg-emerald-600' : 'bg-red-600'
                  }`}>
                    {testResult.passed ? <Award className="w-10 h-10" /> : <AlertCircle className="w-10 h-10" />}
                  </div>

                  <div>
                    <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                      testResult.passed ? 'bg-emerald-200 text-emerald-900' : 'bg-red-200 text-red-900'
                    }`}>
                      {testResult.passed ? 'LULUS EVALUASI (PASSED)' : 'BELUM LULUS (RE-TAKE NEEDED)'}
                    </span>
                    <h3 className="text-3xl font-extrabold mt-3">
                      Nilai Akhir: {testResult.score} / 100
                    </h3>
                    <p className="text-xs mt-2 opacity-90 max-w-md mx-auto">
                      {testResult.passed 
                        ? `Selamat ${identity.fullName}! Anda telah memenuhi standar evaluasi keselamatan kerja untuk modul ${activeModule.title}.`
                        : `Nilai minimal kelulusan adalah 70%. Silakan pelajari ulang materi slide dan ikuti kembali tes evaluasi.`}
                    </p>
                  </div>
                </div>

                {/* Download Certificate Action */}
                {testResult.passed && savedRecord && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4 shadow-sm">
                    <h4 className="text-sm font-bold text-gray-800">
                      Sertifikat Kelulusan Safety Mandatory Training
                    </h4>
                    <p className="text-xs text-gray-500">
                      Sertifikat ini diterbitkan secara resmi atas nama {savedRecord.fullName} ({savedRecord.nik}) dari {savedRecord.company}.
                    </p>

                    <button
                      onClick={() => generateMandatoryTrainingPDF(savedRecord)}
                      className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 text-sm cursor-pointer"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Sertifikat Digital PDF</span>
                    </button>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  {!testResult.passed && (
                    <button
                      onClick={() => setCurrentStep('SLIDES')}
                      className="w-full sm:w-auto px-6 py-3 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 transition text-xs flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Pelajari Ulang & Tes Kembali</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSelectedModuleId(null);
                      setCurrentStep('IDENTITY');
                    }}
                    className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-xl hover:bg-gray-300 transition text-xs cursor-pointer"
                  >
                    Pilih Modul Training Lain
                  </button>
                </div>

              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
};
